import React, { useState, useRef, useEffect } from 'react';
import { UploadClientUserFile } from '../../../Controller/Api/PortalController';
import { ApiBaseResponse, ResponseCode } from '../../../Model/Response/Common/ApiBaseResponse';
import { FileUploadResponse } from '../../../Model/Response/ClientUser/FileUploadResponse';
import { FaSpinner } from 'react-icons/fa'; // Spinner ikonu


// Props tipi tanımlıyoruz
interface FileUploadComponentProps {
    uploadUrl: string;                // Sunucuya gönderim URL'si
    allowedTypes: string[];           // İzin verilen dosya türleri
    maxSizeMB?: number;               // Opsiyonel - Maksimum dosya boyutu (MB)
    onSuccess?: (response: FileUploadResponse) => void; // Artık response alıyor
    onError?: (error: string) => void;// Opsiyonel - Hatalı yükleme sonrası callback
    customStyle?: React.CSSProperties; // Kullanıcı tarafından sağlanan stil
    icon?: React.ReactNode;           // İkonu prop olarak alıyoruz (örn: <FaPaperPlane/>)
    hID: string;                      // hID parametresi
    guid: string;                     // guid parametresi
    sessionID: number;                // sessionID parametresi
}

const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
    uploadUrl,
    allowedTypes,
    maxSizeMB = 10,               // Varsayılan olarak 10 MB
    onSuccess,
    onError,
    customStyle,                  // Kullanıcı tarafından sağlanan stil
    icon,                         // İkon prop'u ekliyoruz
    hID,            // Parametreler alınıyor
    guid,           // Parametreler alınıyor
    sessionID,      // Parametreler alınıyor
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [dragging, setDragging] = useState<boolean>(false); // Dragging durumunu izlemek için
    const fileInputRef = useRef<HTMLInputElement | null>(null); // Dosya inputu için ref kullanıyoruz
    const [loading, setLoading] = useState<boolean>(false); // Dosya yüklenirken loading durumu


    // Dosya seçimi işlemi
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0] || null;
        validateAndSetFile(selectedFile);
    };

    // Sürüklenen dosyayı kontrol et ve ayarla
    const validateAndSetFile = (selectedFile: File | null) => {
        if (selectedFile) {
            const fileSizeMB = selectedFile.size / (1024 * 1024); // MB cinsinden boyut
            if (!allowedTypes.includes(selectedFile.type)) {
                setErrorMessage('Geçersiz dosya türü. Yalnızca belirli dosyalar yüklenebilir.');
                setFile(null);
            } else if (fileSizeMB > maxSizeMB) {
                setErrorMessage(`Dosya boyutu ${maxSizeMB} MB'tan büyük olamaz.`);
                setFile(null);
            } else {
                setFile(selectedFile);
                setErrorMessage('');
                handleFileUpload(selectedFile); // Doğrudan dosya yüklensin
            }
        }
    };

    // Dosya yükleme işlemi
    const handleFileUpload = async (file: File) => {

        setLoading(true);  // Yükleme başladığında loading true
        try {

            let response: ApiBaseResponse<FileUploadResponse> | null =
                await UploadClientUserFile(file, Number(hID), guid, sessionID);

            if (!response || !response.content || response.responseCode != ResponseCode.OK)
                return;

            // Başarılıysa onSuccess callback'ini çağırıyoruz
            if (onSuccess) {
                onSuccess(response.content);  // response.content içeriğini onSuccess ile tetikliyoruz
            }
            console.log(response.content)

        } catch (error) {
            if (error instanceof Error) {
                setErrorMessage('Yükleme sırasında bir hata oluştu: ' + error.message);
                if (onError) onError(error.message);
            } else {
                setErrorMessage('Yükleme sırasında bilinmeyen bir hata oluştu.');
                if (onError) onError('Bilinmeyen hata');
            }
        } finally {
            setLoading(false);  // Yükleme tamamlandığında loading false
        }
    };

    // Sürükle Bırak olay işleyicileri
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setDragging(false);

        const droppedFile = e.dataTransfer.files?.[0] || null;
        validateAndSetFile(droppedFile);
    };

    // Dosya input'una tıklamak için
    const handleClick = () => {
        fileInputRef.current?.click();
    };

    // Varsayılan stil
    const defaultStyle: React.CSSProperties = {
        border: '2px dashed #aaa',
        padding: '20px',
        textAlign: 'center',
        display: 'flex',             // Flexbox ile ortalama
        alignItems: 'center',        // Dikey ortalama
        justifyContent: 'center',    // Yatay ortalama
        height: '150px',             // Alanın boyutu
        backgroundColor: dragging ? '#f0f0f0' : '#fff',
        cursor: 'pointer',
    };

    // Mesajı gösterdikten sonra 3 saniye içinde kaybolmasını sağlamak için useEffect
    useEffect(() => {
        if (errorMessage) {
            const timer = setTimeout(() => {
                setErrorMessage('');  // 3 saniye sonra hata mesajını temizliyoruz
            }, 3000);
            return () => clearTimeout(timer);  // Component unmount olursa temizliyoruz
        }
    }, [errorMessage]);  // errorMessage her değiştiğinde tetiklenir


    return (
        <div className={`file-upload-component`}>
            <div
                className={`drag-drop-area ${dragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
                style={customStyle ? customStyle : defaultStyle}  // customStyle varsa onu kullan, yoksa defaultStyle
            >
                {/* Eğer yükleme yapılıyorsa spinner göster, aksi halde ikon */}
                {loading ? (
                    <FaSpinner className="spinner" size={32} />
                ) : (
                    icon && <div>{icon}</div>  // Eğer yükleme yoksa ikonu göster
                )}
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                    style={{ display: 'none' }} // Input gizli
                />
            </div>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        </div>
    );
};

export default FileUploadComponent;
